import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import React, { Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { UFOSuspenseFallback, useReactUFORouteWatcher } from './analytics/react-ufo';
import { SessionReplayInitializer } from './analytics/sessionReplay/SessionReplayInitializer';
import { AnalyticsUserMetadataProvider } from './bootstrap/AnalyticsUserMetadataProvider';
import { UserAccount } from './clients/gateway-client/types';
import { ConsentBanner } from './components/consent/ConsentBanner';
import { LocalDevBanner } from './components/local-dev-banner';
import { IdentityLogout } from './components/logout/ntp-logout/IdentityLogout';
import MetaTags from './components/MetaTags';
import { bifrostEnv } from './config';
import { useBroadcastProactiveContextMenuVariants } from './hooks/useBroadcastProactiveContextMenuVariants';
import { ExperienceSLO, ExperienceSLOKeys } from './observability';

const SessionLazy = React.lazy(
	() => import(/* webpackChunkName: "session" */ './components/Session'),
);
const PopupLoginRedirectPageLazy = React.lazy(() =>
	import(
		/* webpackChunkName: "popup-login-redirect" */ './components/login/PopupLoginRedirectPage'
	).then((module) => ({
		default: module.PopupLoginRedirectPage,
	})),
);
const NtpLoginRedirectPageLazy = React.lazy(() =>
	import(
		/* webpackChunkName: "ntp-login-redirect" */ './components/login/ntp-login/NtpLoginRedirectPage'
	).then((module) => ({
		default: module.NtpLoginRedirectPage,
	})),
);
const NtpLogoutRedirectPageLazy = React.lazy(() =>
	import(
		/* webpackChunkName: "ntp-logout-redirect" */ './components/logout/ntp-logout/NtpLogoutRedirectPage'
	).then((module) => ({
		default: module.NtpLogoutRedirectPage,
	})),
);
const ChatPageLazy = React.lazy(() =>
	import(/* webpackChunkName: "chat" */ './components/chat/ChatPage').then((module) => ({
		default: module.ChatPage,
	})),
);
const OptionsPageLazy = React.lazy(() =>
	import(/* webpackChunkName: "options" */ './components/options/OptionsPage').then((module) => ({
		default: module.OptionsPage,
	})),
);
const HomeLazy = React.lazy(() => import(/* webpackChunkName: "home" */ './components/home/Home'));
const WelcomePageLazy = React.lazy(
	() => import(/* webpackChunkName: "welcome-page" */ './components/welcome-page/WelcomePage'),
);
const PopupPageLazy = React.lazy(
	() => import(/* webpackChunkName: "popup-page" */ './components/popup-page/PopupPage'),
);
const MenuPageLazy = React.lazy(() =>
	import(/* webpackChunkName: "menu" */ './components/menu-page/MenuPage').then((module) => ({
		default: module.MenuPage,
	})),
);
const PageLayoutLazy = React.lazy(
	() => import(/* webpackChunkName: "page-layout" */ './components/page-layout/PageLayout'),
);
const UninstallPageLazy = React.lazy(
	() =>
		import(/* webpackChunkName: "uninstall-page" */ './components/uninstall-page/UninstallPage'),
);

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			gcTime: 1000 * 60 * 60 * 24, // 24 hours
		},
	},
});

export const persister = createSyncStoragePersister({
	storage: window.localStorage,
});

export default function ExtensionApp({
	userAccount,
}: {
	userAccount: UserAccount;
}): React.JSX.Element {
	// Add route watcher inside router context
	useReactUFORouteWatcher();

	// Broadcast Rovo Context Menu variants once for the content-script overlay to read.
	useBroadcastProactiveContextMenuVariants();

	const isDevBuild = bifrostEnv === 'dev';

	return (
		<AnalyticsUserMetadataProvider>
			<SessionReplayInitializer />
			<ExperienceSLO ufo={ExperienceSLOKeys.APP_ROOT}>
				<Suspense fallback={<UFOSuspenseFallback name="app-root-suspense" />}>
					<MetaTags />
					<ConsentBanner />
					{isDevBuild && <LocalDevBanner />}
					<Routes>
						<Route path="/extension">
							<Route Component={WelcomePageLazy} path="welcome" />
							<Route Component={PopupLoginRedirectPageLazy} path="login-redirect" />
							<Route Component={IdentityLogout} path="log-out" />
							<Route Component={NtpLoginRedirectPageLazy} path="ntp-login-redirect" />
							<Route Component={NtpLogoutRedirectPageLazy} path="ntp-logout-redirect" />
							<Route Component={PopupPageLazy} path="popup" />
							<Route Component={UninstallPageLazy} path="uninstall" />
							<Route element={<PageLayoutLazy userAccount={userAccount} />}>
								<Route element={<MenuPageLazy userAccount={userAccount} />} path="menu" />
								<Route element={<OptionsPageLazy userAccount={userAccount} />} path="options" />
								<Route element={<HomeLazy userAccount={userAccount} />} path="" />
								<Route element={<ChatPageLazy userAccount={userAccount} />} path="rovo-chat" />
							</Route>
							<Route element={<SessionLazy />} path="session" />
						</Route>
					</Routes>
				</Suspense>
			</ExperienceSLO>
		</AnalyticsUserMetadataProvider>
	);
}
